import React, { useCallback, useContext, useMemo } from 'react';
import useColumnOrder from '../../PerformanceBoards/PerformanceBoardSettings/SettingsWizard/hooks/useColumnOrder';
import Typography from '../../../kingpin/atoms/Typography';
import ColumnSizeSettingsStyles from './styles';
import useGetFieldLabel from '../../../hooks/useGetFieldLabel';
import PerformanceBoardSettingsContext from '../../PerformanceBoards/PerformanceBoardSettings/contexts/PerformanceBoardSettingsContext';
import Button from '../../../kingpin/atoms/Button';
import Row from '../../Common/Row';
import Dropdown from '../Dropdown';

const SpecificField = ({
  columnSizeStrategy,
  setColumnSizeStrategy,
  columnSettings,
}: {
  columnSizeStrategy: FleetOps.ColumnSizeStrategies.FitGridWidth;
  setColumnSizeStrategy: React.Dispatch<
    React.SetStateAction<FleetOps.ColumnSizeStrategies.Strategy | undefined>
  >;
  columnSettings: {
    field: string;
    minWidth?: number;
    maxWidth?: number;
  };
}) => {
  const { board } = useContext(PerformanceBoardSettingsContext);
  const { columnOrder } = useColumnOrder();
  const isProgressField = columnOrder.some(
    (c) => c.field === columnSettings.field && c.type === 'progress',
  );
  const { getFieldLabel } = useGetFieldLabel();

  const isMinWidthEnabled = columnSettings.minWidth !== undefined;
  const isMaxWidthEnabled = columnSettings.maxWidth !== undefined;

  const onMinWidthToggled = useCallback(() => {
    setColumnSizeStrategy({
      ...columnSizeStrategy,
      columnLimits: columnSizeStrategy.columnLimits.map((cl) => {
        if (cl.field === columnSettings.field) {
          return {
            ...cl,
            minWidth: isMinWidthEnabled ? undefined : 80,
          };
        }
        return cl;
      }),
    });
  }, [
    columnSettings.field,
    columnSizeStrategy,
    isMinWidthEnabled,
    setColumnSizeStrategy,
  ]);

  const onMaxWidthToggled = useCallback(() => {
    setColumnSizeStrategy({
      ...columnSizeStrategy,
      columnLimits: columnSizeStrategy.columnLimits.map((cl) => {
        if (cl.field === columnSettings.field) {
          return {
            ...cl,
            maxWidth: isMaxWidthEnabled ? undefined : 80,
          };
        }
        return cl;
      }),
    });
  }, [
    columnSettings.field,
    columnSizeStrategy,
    isMaxWidthEnabled,
    setColumnSizeStrategy,
  ]);

  const onMinWidthChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setColumnSizeStrategy({
        ...columnSizeStrategy,
        columnLimits: columnSizeStrategy.columnLimits.map((cl) => {
          if (cl.field === columnSettings.field) {
            return {
              ...cl,
              minWidth: Number.parseInt(event.target.value),
            };
          }
          return cl;
        }),
      });
    },
    [columnSettings.field, columnSizeStrategy, setColumnSizeStrategy],
  );

  const onMaxWidthChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setColumnSizeStrategy({
        ...columnSizeStrategy,
        columnLimits: columnSizeStrategy.columnLimits.map((cl) => {
          if (cl.field === columnSettings.field) {
            return {
              ...cl,
              maxWidth: Number.parseInt(event.target.value),
            };
          }
          return cl;
        }),
      });
    },
    [columnSettings.field, columnSizeStrategy, setColumnSizeStrategy],
  );

  const onRemoveClicked = useCallback(() => {
    setColumnSizeStrategy({
      ...columnSizeStrategy,
      columnLimits: columnSizeStrategy.columnLimits.filter((cl) => {
        return cl.field !== columnSettings.field;
      }),
    });
  }, [columnSettings.field, columnSizeStrategy, setColumnSizeStrategy]);
  return (
    <div
      style={{
        padding: 8,
        borderRadius: 4,
        border: '1px solid #eaeaea',
        marginBottom: 16,
      }}
    >
      <ColumnSizeSettingsStyles.Section>
        <div style={{ marginBottom: 4 }}>
          <Row spaceBetween centerAlign>
            <Typography.Body type={'Body Bold'}>
              {getFieldLabel({
                field: columnSettings.field,
                dataType: board.dataType,
              })}
              {isProgressField ? '(Progress Field)' : ''}
            </Typography.Body>
            <Button
              size="Small"
              type={'Secondary'}
              icon={'cross'}
              onClick={onRemoveClicked}
            />
          </Row>
        </div>
      </ColumnSizeSettingsStyles.Section>
      <ColumnSizeSettingsStyles.Section>
        <ColumnSizeSettingsStyles.OptionalNumberInput
          label={'Min Width'}
          isEnabled={isMinWidthEnabled}
          onToggled={onMinWidthToggled}
          value={columnSettings.minWidth}
          onChange={onMinWidthChanged}
          testIdPrefix={`${columnSettings.field}-min`}
        />
      </ColumnSizeSettingsStyles.Section>
      <ColumnSizeSettingsStyles.Section>
        <ColumnSizeSettingsStyles.OptionalNumberInput
          label={'Max Width'}
          isEnabled={isMaxWidthEnabled}
          onToggled={onMaxWidthToggled}
          value={columnSettings.maxWidth}
          onChange={onMaxWidthChanged}
          testIdPrefix={`${columnSettings.field}-max`}
        />
      </ColumnSizeSettingsStyles.Section>
    </div>
  );
};

const SpecificFieldSettings = ({
  fields,
  columnSizeStrategy,
  setColumnSizeStrategy,
}: {
  fields: { key?: string; field: string; label: string }[];
  columnSizeStrategy: FleetOps.ColumnSizeStrategies.FitGridWidth;
  setColumnSizeStrategy: React.Dispatch<
    React.SetStateAction<FleetOps.ColumnSizeStrategies.Strategy | undefined>
  >;
}) => {
  const options = useMemo((): DropdownOption[] => {
    return fields.map(({ key, field, label }) => {
      const isSelected = columnSizeStrategy.columnLimits.some(
        (sc) => sc.field === field,
      );

      return {
        key: key || field,
        label,
        isSelected: false,
        onSelected: () => {
          if (isSelected) {
            setColumnSizeStrategy({
              ...columnSizeStrategy,
              columnLimits: columnSizeStrategy.columnLimits.filter(
                (c) => c.field !== field,
              ),
            });
            return;
          }

          setColumnSizeStrategy({
            ...columnSizeStrategy,
            columnLimits: [...columnSizeStrategy.columnLimits, { field }],
          });
        },
      };
    });
  }, [columnSizeStrategy, fields, setColumnSizeStrategy]);

  return (
    <>
      <div style={{ marginBottom: 8 }}>
        <Typography.Header type={'H5'}>
          Specific Field Settings
        </Typography.Header>
      </div>
      {columnSizeStrategy.columnLimits.map((col, index) => (
        <SpecificField
          key={`${col.field}-${index}`}
          columnSizeStrategy={columnSizeStrategy}
          setColumnSizeStrategy={setColumnSizeStrategy}
          columnSettings={col}
        />
      ))}
      <Dropdown options={options} placeholder="Add Specific Field Settings" />
    </>
  );
};

export default SpecificFieldSettings;
